/**
 * Pending signups queue — accounts that self-registered and need
 * operator approval before they can log in.
 *
 * Row state tells you what's blocking each one:
 *  - "verified" badge: the user clicked the link in their email →
 *    approve them if the email + display name look real.
 *  - no badge: they haven't verified yet; safer to wait or reject
 *    if the email looks fake/throwaway.
 *
 * Approving sends a "your account is approved" email automatically.
 * Rejecting takes a free-form reason that goes into the audit chain
 * (NOT in the rejection email).
 */

import { Alert, Badge, Button, Group, Paper, Stack, Table, Text, Title } from "@mantine/core";
import { useEffect, useState } from "react";

import { ApiError, api, type SignupReviewRow } from "../lib/api";

export function SignupsPanel() {
  const [rows, setRows] = useState<SignupReviewRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const refresh = async () => {
    try {
      const r = await api.signupsList();
      setRows(r.signups);
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : "failed to load signups");
    }
  };

  useEffect(() => { refresh(); }, []);

  const approve = async (row: SignupReviewRow) => {
    if (!row.email_verified) {
      if (!window.confirm(
        `${row.email} has NOT verified their email yet. Approve anyway?\n\n` +
        "Approving unverified signups makes spam/typo accounts harder to spot. " +
        "Recommended: wait for verification first."
      )) return;
    } else if (!window.confirm(`Approve ${row.email}? They'll be able to log in immediately.`)) {
      return;
    }
    setBusyId(row.account_id);
    try {
      await api.approveSignup(row.account_id);
      await refresh();
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : "approve failed");
    } finally {
      setBusyId(null);
    }
  };

  const reject = async (row: SignupReviewRow) => {
    const reason = window.prompt(
      `Reject ${row.email}? Enter a reason (audited, not emailed to user):`,
    );
    if (!reason || !reason.trim()) return;
    setBusyId(row.account_id);
    try {
      await api.rejectSignup(row.account_id, reason.trim());
      await refresh();
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : "reject failed");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Stack>
      <Group justify="space-between">
        <Title order={2}>Pending signups</Title>
        <Button variant="light" onClick={refresh}>Refresh</Button>
      </Group>
      <Text c="dimmed" size="sm">
        Accounts that self-registered via the public signup form and are
        waiting for operator approval. Until you approve, login is refused
        with a "waiting for approval" message. Approving sends an auto
        email; rejecting blocks all future logins and revokes any session.
      </Text>
      {error && <Alert color="red">{error}</Alert>}
      {rows === null ? (
        <Text>Loading…</Text>
      ) : rows.length === 0 ? (
        <Paper p="xl" withBorder>
          <Text c="dimmed" ta="center">No pending signups.</Text>
        </Paper>
      ) : (
        <Paper withBorder>
          <Table striped>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Email</Table.Th>
                <Table.Th>Display name</Table.Th>
                <Table.Th>Verified</Table.Th>
                <Table.Th>Requested</Table.Th>
                <Table.Th />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows.map((row) => (
                <Table.Tr key={row.account_id}>
                  <Table.Td>{row.email}</Table.Td>
                  <Table.Td>{row.display_name}</Table.Td>
                  <Table.Td>
                    {row.email_verified ? (
                      <Badge color="green">verified</Badge>
                    ) : (
                      <Badge color="yellow">unverified</Badge>
                    )}
                  </Table.Td>
                  <Table.Td>{new Date(row.created_at).toLocaleString()}</Table.Td>
                  <Table.Td>
                    <Group gap={6}>
                      <Button
                        size="xs" color="green" variant="light"
                        loading={busyId === row.account_id}
                        onClick={() => approve(row)}
                      >
                        Approve
                      </Button>
                      <Button
                        size="xs" color="red" variant="light"
                        loading={busyId === row.account_id}
                        onClick={() => reject(row)}
                      >
                        Reject
                      </Button>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Paper>
      )}
    </Stack>
  );
}
